import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {TranslatePipe} from '@brunata-angular/util';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {filter, map} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectBuilding} from 'src/app/state/selectors';

type Card = {
    routePath: string;
    icon: string;
    title: string;
    subtitle: string;
};

@Component({
    selector: 'app-other-agents',
    standalone: true,
    imports: [
        TranslatePipe,
        CommonModule,
        RouterLink,
        RouterLinkActive,
        RouterOutlet,
        NgOptimizedImage,
    ],
    templateUrl: './other-agents.component.html',
    styleUrl: './other-agents.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtherAgentsComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly store = inject(Store);
    private readonly hasHumidityMeters$: Observable<boolean> = this.store
        .select(selectBuilding)
        .pipe(
            map(
                buildingState =>
                    buildingState.status === 'success' &&
                    buildingState.buildingDetails.meterAggregations.some(
                        it => it.allocationUnit === 'R' && it.count > 0,
                    ),
            ),
        );
    private readonly showCards$ = new BehaviorSubject<boolean>(true);

    protected readonly displayOptions$ = combineLatest({
        showCards: this.showCards$,
        hasHumidityMeters: this.hasHumidityMeters$,
    });

    readonly cards: Card[] = [
        {
            routePath: 'humidity',
            icon: 'assets/icons/Humidity_blue.svg',
            title: 'titles_humidityMonitorReport',
            subtitle: 'titles_create-tile.humidity',
        },
        {
            routePath: 'missing-readings',
            icon: 'assets/icons/Missing_reading_blue.svg',
            title: 'titles_missingReadings',
            subtitle: 'titles_create-tile.temperature',
        },
    ];

    constructor(private router: Router) {}

    ngOnInit(): void {
        this.setupNavigationListener();
    }

    private setupNavigationListener(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({urlAfterRedirects}) => {
                this.showCards$.next(!this.isCardRoute(urlAfterRedirects));
            });
    }

    private isCardRoute(url: string): boolean {
        return this.cards.some(card => url.includes(card.routePath));
    }
}
